import React, { useEffect, useMemo, useState } from "react";
import { Button, Checkbox, DatePicker, Input, Modal, message } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import styled from "styled-components";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import UseApi from "../../Reusable/CallApi/UseApi";
import DonutChart from "../../Reusable/Charts/DountChart";

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;

  .card {
    border: 1px solid #eee;
    border-radius: 10px;
    padding: 20px;
  }

  .card-title {
    font-size: 16px;
    font-weight: 500;
    color: #1f243b;
    margin-bottom: 16px;
  }

  .todo-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .todo-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 0;
    border-bottom: 1px solid #f5f5f5;
  }

  .todo-due {
    color: #68728a;
    font-size: 12px;
  }

  /* Mint green, matching Administrator Dashboard's own "+ Create"
     to-do button (Dashboard/style.js's .to-do-button) exactly. */
  .mint-button {
    background: #c2fae5;
    border-color: #c2fae5;
    color: #1f243b;
  }
  .mint-button:hover,
  .mint-button:focus {
    background: #aef0d4 !important;
    border-color: #aef0d4 !important;
    color: #1f243b !important;
  }
`;

const PROGRESS_COLORS = {
  Completed: "#33cc7f",
  "In Progress": "#ff943b",
  "Not Started": "#5b6bff",
  "Not Passed": "#ff4d6a",
};

// There's no separate learner login anywhere in this app - see
// learnerMenuItem's comment in Redux/systemSlice.js. The progress donut
// is computed from the selected person's real getmycourses data. To-dos
// reuse the same client-wide list Administrator/Tutor see (the schema
// has no per-person to-do concept) - real data, just not person-scoped.
const LearnerDashboard = () => {
  const clientId = useSelector((state) => state.authTracking.userInfo.clientId);
  const viewingAsPeopleId = useSelector((state) => state.systemTracking.viewingAsPeopleId);
  const { data, loading, request } = UseApi();
  const { data: todoData, request: todoRequest } = UseApi();

  const [open, setOpen] = useState(false);
  const [newTodoText, setNewTodoText] = useState("");
  const [dueDate, setDueDate] = useState(dayjs());

  useEffect(() => {
    if (!clientId || !viewingAsPeopleId) return;
    request({
      endPoint: `/course/getmycourses/${clientId}?people_id=${viewingAsPeopleId}`,
      method: "get",
    }).catch(() => {});
  }, [clientId, viewingAsPeopleId]);

  const fetchTodos = () => {
    if (!clientId) return;
    todoRequest({ endPoint: `/dashboard/gettodolist/${clientId}`, method: "get" }).catch(() => {});
  };

  useEffect(() => {
    fetchTodos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const courses = data?.response || [];
  const todos = todoData?.response || [];

  const chartData = useMemo(() => {
    const total = courses.length;
    const completed = courses.filter((c) => c.status === "completed").length;
    const inProgress = courses.filter((c) => c.status === "in_progress").length;
    const toPercent = (count) => (total ? Math.round((count / total) * 100) : 0);
    return [
      { type: "Completed", value: toPercent(completed), color: PROGRESS_COLORS.Completed },
      { type: "In Progress", value: toPercent(inProgress), color: PROGRESS_COLORS["In Progress"] },
      { type: "Not Started", value: 0, color: PROGRESS_COLORS["Not Started"] },
      { type: "Not Passed", value: 0, color: PROGRESS_COLORS["Not Passed"] },
    ];
  }, [courses]);

  const overallPercent = courses.length
    ? Math.round(
        courses.reduce((sum, c) => sum + Number(c.progress || 0), 0) / courses.length
      )
    : 0;

  const handleAddTodo = async () => {
    if (!newTodoText.trim()) {
      message.error("Enter a to-do first");
      return;
    }
    try {
      await todoRequest({
        endPoint: `/dashboard/addtodolist/${clientId}`,
        method: "post",
        data: { task_name: [newTodoText.trim()], due_date: dueDate.format("YYYY-MM-DD") },
      });
      message.success("To-do added");
      setOpen(false);
      setNewTodoText("");
      fetchTodos();
    } catch (error) {
      message.error("Failed to add to-do");
    }
  };

  const handleCompleteTodo = async (taskId) => {
    try {
      await todoRequest({ endPoint: `/dashboard/deletetodolist/${clientId}/${taskId}`, method: "delete" });
      fetchTodos();
    } catch (error) {
      message.error("Failed to update to-do");
    }
  };

  if (!viewingAsPeopleId) {
    return (
      <Wrapper>
        <div className="card-title">Dashboard</div>
        <div>
          Pick who you're viewing as from the "Learner" dropdown in the header
          to see their real course progress.
        </div>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <div className="card">
        <div className="card-title">My Course Progress</div>
        {!loading && (
          <DonutChart chartData={chartData} centerText={`${overallPercent}%`} />
        )}
      </div>

      <div className="card">
        <div className="todo-header">
          <div className="card-title">My To-dos</div>
          <Button className="mint-button" icon={<PlusOutlined />} onClick={() => setOpen(true)}>
            Add
          </Button>
        </div>
        {todos.map((item) => (
          <div className="todo-item" key={item.task_id}>
            <Checkbox onChange={() => handleCompleteTodo(item.task_id)}>{item.task_name}</Checkbox>
            <span className="todo-due">
              Due: {item.due_date ? dayjs(item.due_date).format("Do MMM") : "-"}
            </span>
          </div>
        ))}
        {todos.length === 0 && <div>No to-dos yet.</div>}
      </div>

      <Modal
        title="Add To-do"
        open={open}
        onCancel={() => setOpen(false)}
        onOk={handleAddTodo}
        okText="Add"
        okButtonProps={{ className: "mint-button" }}
      >
        <Input
          placeholder="What needs to be done?"
          value={newTodoText}
          onChange={(e) => setNewTodoText(e.target.value)}
          style={{ marginBottom: 12 }}
        />
        <DatePicker value={dueDate} onChange={setDueDate} style={{ width: "100%" }} />
      </Modal>
    </Wrapper>
  );
};

export default LearnerDashboard;
